import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Loader2, Save, Plus, Trash2, Upload, ImageIcon, ShieldCheck, Crown, KeyRound, Copy, ArrowLeft, Package, RefreshCw, X, CheckSquare, Square } from 'lucide-react';
import { AddInvoicedSupplier } from '../components/AddInvoicedSupplier';
import { SupplierTypeReview } from '../components/SupplierTypeReview';
import { MovedToProviders } from '../components/MovedToProviders';
import { ProductRichEditor, RichList, GalleryUpload, RankedPicker } from './public/SupplierClaim';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const csv = (a) => (a || []).join(', ');
const toArr = (s) => s.split(',').map((x) => x.trim()).filter(Boolean);

const AdminImageUpload = ({ companyId, value, onChange, label, kind, aspect = 'aspect-video', testid }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('Image must be under 25MB'); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/directory/suppliers/${companyId}/upload-image?kind=${kind}`, fd);
      onChange(data.url); toast.success(`${label} uploaded`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      {value
        ? <div className={`${aspect} w-24 rounded-md overflow-hidden border border-gray-200 bg-gray-50 shrink-0`}><img src={value} alt={label} className="w-full h-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} /></div>
        : <div className={`${aspect} w-24 rounded-md border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center shrink-0`}><ImageIcon className="w-5 h-5 text-gray-300" /></div>}
      <div className="flex flex-col gap-1.5">
        <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#00857D] cursor-pointer hover:text-[#00A79D]" data-testid={`${testid}-btn`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{value ? `Replace ${label}` : `Upload ${label}`}
          <input type="file" accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml,image/heic,image/heif,.heic,.heif" className="hidden" onChange={pick} disabled={busy} />
        </label>
        {value && <button type="button" onClick={() => onChange('')} className="text-xs text-gray-400 hover:text-red-500 text-left">Remove</button>}
      </div>
    </div>
  );
};

const SupplierEditor = () => {
  const [movedKey, setMovedKey] = useState(0);
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);       // full supplier being edited
  const [L, setL] = useState(null);           // listing draft
  const [saving, setSaving] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginUrl, setLoginUrl] = useState('');
  const [selected, setSelected] = useState({});   // id -> true
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showSync, setShowSync] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [charCatalogue, setCharCatalogue] = useState({});
  const [meta, setMeta] = useState({ categories: [], care_types: [], resident_types: [], specialisms: [] });
  const [account, setAccount] = useState(null);
  const [expiryDraft, setExpiryDraft] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserUrl, setNewUserUrl] = useState('');

  const loadAccount = useCallback(async (id) => {
    try {
      const { data } = await axios.get(`${API}/admin/directory/suppliers/${id}/account`);
      setAccount(data);
      setExpiryDraft((data.subscription_expires_at || '').slice(0, 10));
    } catch (e) { setAccount(null); }
  }, []);

  const saveExpiry = async () => {
    try {
      await axios.put(`${API}/admin/directory/suppliers/${sel.id}/subscription`, { expires_at: expiryDraft || null });
      toast.success(expiryDraft ? 'Subscription expiry updated' : 'Subscription expiry cleared');
      loadAccount(sel.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update expiry'); }
  };

  const addUser = async () => {
    const email = (newUserEmail || '').trim();
    if (!email.includes('@')) { toast.error('Enter a valid email'); return; }
    try {
      const { data } = await axios.post(`${API}/admin/directory/suppliers/${sel.id}/add-user`, { email });
      setNewUserUrl(data.set_password_url || '');
      setNewUserEmail('');
      toast.success(data.set_password_url ? 'User added - share the set-password link' : 'User already had access');
      loadAccount(sel.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add user'); }
  };

  const reactivate = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/directory/suppliers/${sel.id}/reactivate`);
      toast.success(`Reactivated as ${data.tier} until ${new Date(data.subscription_expires_at).toLocaleDateString('en-GB')}`);
      loadAccount(sel.id);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not reactivate'); }
  };

  useEffect(() => {
    axios.get(`${API}/public/meta`).then(({ data }) => {
      setCharCatalogue(data.characteristics || {});
      setMeta({ categories: data.categories || [], care_types: data.care_types || [], resident_types: data.resident_types || [], specialisms: data.specialisms || [] });
    }).catch(() => {});
  }, []);

  const search = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/admin/directory/suppliers`, { params: { q: q || undefined } }); setItems(data.items); setTotal(data.total); }
    catch { setItems([]); } finally { setLoading(false); }
  }, [q]);
  useEffect(() => { const t = setTimeout(search, 300); return () => clearTimeout(t); }, [search]);

  const open = async (id) => {
    try {
      const { data } = await axios.get(`${API}/admin/directory/suppliers/${id}`);
      setSel(data); setL({ name: data.name, premium: data.premium, verified: data.verified, website: data.website, phone: data.phone, ...data.listing });
      setNewUserUrl(''); setLoginUrl('');
      loadAccount(id);
      setLoginEmail(data.login_email || ''); setLoginUrl('');
    } catch { toast.error('Could not load supplier'); }
  };

  const patch = (o) => setL((p) => ({ ...p, ...o }));
  const setProduct = (i, o) => setL((p) => ({ ...p, products: p.products.map((x, j) => (j === i ? { ...x, ...o } : x)) }));

  const adminUpload = useCallback(async (file, kind) => {
    const fd = new FormData(); fd.append('file', file);
    const { data } = await axios.post(`${API}/admin/directory/suppliers/${sel.id}/upload-image?kind=${kind}`, fd);
    return data.url;
  }, [sel]);

  const adminAiDraft = async (i) => {
    try {
      const { data } = await axios.post(`${API}/admin/directory/suppliers/${sel.id}/ai-draft-content`, { product_index: i });
      const rc = data.rich_content || {}; const lf = data.listing || {};
      setL((prev) => {
        const products = [...(prev.products || [])];
        products[i] = { ...products[i], rich_content: { ...(products[i].rich_content || {}), ...rc } };
        return {
          ...prev, products,
          description: lf.description || prev.description,
          tagline: lf.tagline || prev.tagline,
          categories: (lf.categories && lf.categories.length) ? lf.categories : prev.categories,
        };
      });
      toast.success('AI draft ready - review the content and hit Save');
      return null;
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not draft content'); return null; }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/directory/suppliers/${sel.id}`, {
        name: L.name, premium: L.premium, verified: L.verified, website: L.website, phone: L.phone, tagline: L.tagline, description: L.description,
        logo_url: L.logo_url, categories: L.categories, care_types: L.care_types, resident_types: L.resident_types, specialisms: L.specialisms,
        products: L.products, videos: L.videos, faqs: L.faqs, accordions: L.accordions, testimonials: L.testimonials, screenshots: L.screenshots,
        tier: L.tier, supplier_type: L.supplier_type, stage: L.stage || 'unknown',
        supplier_reviewed: !!L.supplier_reviewed, product_reviewed: !!L.product_reviewed,
        characteristics: L.characteristics || {}, podcast: L.podcast || {}, intel: L.intel || {},
      });
      setSel(data); setL({ name: data.name, premium: data.premium, verified: data.verified, website: data.website, phone: data.phone, ...data.listing });
      toast.success('Saved - changes are live'); search();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setSaving(false); }
  };

  const assignLogin = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/directory/suppliers/${sel.id}/assign-login`, { email: loginEmail });
      setLoginUrl(data.set_password_url); toast.success('Login created - copy the link below'); search();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create login'); }
  };

  const selCount = Object.values(selected).filter(Boolean).length;
  const toggleSel = (id) => setSelected((p) => ({ ...p, [id]: !p[id] }));
  const bulkFlag = async (patchObj) => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/directory/bulk-flags`, { ids, ...patchObj });
      toast.success(`Updated ${data.updated} supplier${data.updated === 1 ? '' : 's'}`);
      setSelected({}); search();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk update failed'); } finally { setBulkBusy(false); }
  };
  const runSync = async () => {
    setSyncing(true); setSyncResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/directory/sync-import`);
      setSyncResult(data.summary); toast.success('Directory data synced');
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); } finally { setSyncing(false); }
  };

  /* ---------------- list view ---------------- */
  if (!sel) {
    return (
      <div className="p-6 max-w-5xl mx-auto" data-testid="supplier-editor">
        <div className="flex items-start justify-between gap-4 mb-1">
          <div className="flex items-center gap-2"><Package className="w-6 h-6 text-[#00A79D]" /><h1 className="font-heading text-2xl font-bold text-gray-900">Supplier & Product Editor</h1></div>
          <div className="flex gap-2 shrink-0"><AddInvoicedSupplier />
          <button onClick={() => { setShowSync(true); setSyncResult(null); }} data-testid="open-sync" className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#00A79D] text-[#00857D] text-sm font-semibold rounded-lg hover:bg-[#00A79D]/5 shrink-0"><RefreshCw className="w-4 h-4" />Sync directory data</button></div>
        </div>
        <p className="text-sm text-gray-500 mb-5">Edit any supplier's listing and products directly, or create a login so the supplier can manage it themselves.</p>
        <SupplierTypeReview onDone={() => { search(); setMovedKey((k) => k + 1); }} />
        <MovedToProviders refreshKey={movedKey} onRestored={search} />
        <div className="relative mb-4">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search suppliers or products…" data-testid="supplier-search" className="w-full border border-gray-200 rounded-lg pl-9 pr-3 py-2.5 text-sm" />
        </div>

        {selCount > 0 && (
          <div className="sticky top-2 z-10 flex flex-wrap items-center gap-2 bg-[#00A79D]/10 border border-[#00A79D]/30 rounded-lg px-3 py-2 mb-3" data-testid="bulk-toolbar">
            <span className="text-sm font-semibold text-[#00857D]">{selCount} selected</span>
            <div className="flex-1" />
            <button disabled={bulkBusy} onClick={() => bulkFlag({ premium: true })} data-testid="bulk-premium-on" className="text-xs font-semibold px-2.5 py-1.5 rounded bg-[#00A79D] text-white hover:bg-[#00857D]">Mark Premium</button>
            <button disabled={bulkBusy} onClick={() => bulkFlag({ premium: false })} data-testid="bulk-premium-off" className="text-xs font-semibold px-2.5 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Remove Premium</button>
            <button disabled={bulkBusy} onClick={() => bulkFlag({ verified: true })} data-testid="bulk-verified-on" className="text-xs font-semibold px-2.5 py-1.5 rounded bg-[#00857D] text-white hover:bg-[#006b64]">Mark Verified</button>
            <button disabled={bulkBusy} onClick={() => bulkFlag({ verified: false })} data-testid="bulk-verified-off" className="text-xs font-semibold px-2.5 py-1.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50">Remove Verified</button>
            <button onClick={() => setSelected({})} className="text-gray-400 hover:text-gray-700 ml-1"><X className="w-4 h-4" /></button>
          </div>
        )}

        <p className="text-xs text-gray-400 mb-2">{total} suppliers</p>
        {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div> : (
          <div className="space-y-2">
            {items.map((s) => (
              <div key={s.id} data-testid={`supplier-row-${s.slug}`} className={`bg-white border rounded-lg px-4 py-3 flex items-center gap-3 transition-colors ${selected[s.id] ? 'border-[#00A79D]' : 'border-gray-200'}`}>
                <button onClick={() => toggleSel(s.id)} data-testid={`select-${s.slug}`} className="text-[#00A79D] shrink-0">
                  {selected[s.id] ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5 text-gray-300" />}
                </button>
                <button onClick={() => open(s.id)} className="flex-1 text-left flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap"><span className="font-semibold text-gray-900">{s.name}</span>
                      {s.premium && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-white bg-[#00A79D] px-1.5 py-0.5 rounded"><Crown className="w-3 h-3" />Premium</span>}
                      {s.verified && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#00857D] bg-[#00A79D]/10 px-1.5 py-0.5 rounded"><ShieldCheck className="w-3 h-3" />Verified</span>}
                      {s.hidden && <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">Hidden</span>}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{s.product_count} products · {s.has_login ? 'has login' : 'no login'}</div>
                  </div>
                  <span className="text-xs font-semibold text-[#00857D]">Edit →</span>
                </button>
              </div>
            ))}
          </div>
        )}

        {showSync && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="sync-dialog">
            <div className="bg-white rounded-xl max-w-lg w-full p-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading text-lg font-bold text-gray-900">Sync directory data</h3>
                <button onClick={() => setShowSync(false)} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-sm text-gray-600 mb-4">This imports the full supplier catalogue (products, descriptions and images) into <b>this environment's database</b>. Run it on production to restore missing products. It's safe to run repeatedly - it only adds what's missing and never deletes anything.</p>
              {!syncResult ? (
                <button onClick={runSync} disabled={syncing} data-testid="run-sync" className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#00A79D] text-white text-sm font-semibold rounded-lg hover:bg-[#00857D] disabled:opacity-60">
                  {syncing ? <><Loader2 className="w-4 h-4 animate-spin" />Syncing…</> : <><RefreshCw className="w-4 h-4" />Run sync now</>}
                </button>
              ) : (
                <div className="bg-[#00A79D]/5 border border-[#00A79D]/20 rounded-lg p-4 text-sm text-gray-700 space-y-1" data-testid="sync-result">
                  <p>✅ Synced <b>{syncResult.suppliers_in_snapshot}</b> suppliers.</p>
                  <p>Created <b>{syncResult.created}</b> · updated <b>{syncResult.updated}</b> · products added <b>{syncResult.products_added}</b>.</p>
                  <p>The Access Group now has <b>{syncResult.access_group_products}</b> products.</p>
                  <button onClick={() => { setShowSync(false); search(); }} className="mt-2 text-[#00857D] font-semibold">Done</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ---------------- editor view ---------------- */
  return (
    <div className="p-6 max-w-3xl mx-auto pb-24" data-testid="supplier-editor-detail">
      <button onClick={() => setSel(null)} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-4"><ArrowLeft className="w-4 h-4" />Back to list</button>

      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700">Supplier name</label>
          <input value={L.name || ''} onChange={(e) => patch({ name: e.target.value })} data-testid="edit-name" className="w-full mt-1 border border-gray-200 rounded-md px-3 py-2.5 text-sm" />
        </div>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700"><input type="checkbox" checked={!!L.premium} onChange={(e) => patch({ premium: e.target.checked })} data-testid="edit-premium" />Premium</label>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700"><input type="checkbox" checked={!!L.verified} onChange={(e) => patch({ verified: e.target.checked })} data-testid="edit-verified" />Verified</label>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Logo</label>
          <div className="mt-1"><AdminImageUpload companyId={sel.id} value={L.logo_url} onChange={(url) => patch({ logo_url: url })} label="logo" kind="logo" aspect="aspect-square" testid="edit-logo-upload" /></div>
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Tagline</label>
          <input value={L.tagline || ''} onChange={(e) => patch({ tagline: e.target.value })} data-testid="edit-tagline" className="w-full mt-1 border border-gray-200 rounded-md px-3 py-2.5 text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Description</label>
          <textarea rows={4} value={L.description || ''} onChange={(e) => patch({ description: e.target.value })} data-testid="edit-description" className="w-full mt-1 border border-gray-200 rounded-md px-3 py-2.5 text-sm" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Categories</label>
          <RankedPicker testid="edit-categories" options={meta.categories} items={L.categories || []} onChange={(v) => patch({ categories: v })} max={3} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Care types</label>
            <RankedPicker testid="edit-caretypes" options={meta.care_types} items={L.care_types || []} onChange={(v) => patch({ care_types: v })} max={3} />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Resident types</label>
            <RankedPicker testid="edit-residenttypes" options={meta.resident_types} items={L.resident_types || []} onChange={(v) => patch({ resident_types: v })} max={3} />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Specialisms</label>
            <RankedPicker testid="edit-specialisms" options={meta.specialisms} items={L.specialisms || []} onChange={(v) => patch({ specialisms: v })} max={3} />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Website URL</label>
            <input type="url" value={L.website || ''} onChange={(e) => patch({ website: e.target.value })} data-testid="edit-website" placeholder="https://www.example.co.uk" className="w-full mt-1 border border-gray-200 rounded-md px-3 py-2.5 text-sm" />
            <p className="text-[11px] text-gray-400 mt-1">Shown as the "Visit website" link on the public supplier page.</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Phone number</label>
            <input type="tel" value={L.phone || ''} onChange={(e) => patch({ phone: e.target.value })} data-testid="edit-phone" placeholder="e.g. 0161 123 4567" className="w-full mt-1 border border-gray-200 rounded-md px-3 py-2.5 text-sm" />
            <p className="text-[11px] text-gray-400 mt-1">Included in CRM list exports.</p>
          </div>
        </div>
      </div>

      {/* Search strength & tier */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mt-5" data-testid="strength-editor">
        <div className="flex items-center gap-2 mb-1"><Crown className="w-5 h-5 text-[#00A79D]" /><h3 className="font-heading font-semibold text-gray-900">Search strength &amp; tier</h3></div>
        <p className="text-sm text-gray-500 mb-4">The <strong>Categories</strong> and <strong>Care types</strong> pickers above are the single source of truth: their order drives search ranking. <strong>Top 3</strong> (positions 1-3) = what they're strongest in; the rest are <strong>"also covers"</strong>. <strong>Dormant</strong> (free/unclaimed) shows only their #1 category; <strong>Confirmed</strong> (paying) shows their Top 3. Tier ladder low→high: Dormant · Confirmed · Partner · Gold Partner.</p>
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Tier</label>
            <select value={L.tier || 'unverified'} onChange={(e) => patch({ tier: e.target.value })} data-testid="edit-tier" className="w-full mt-1 border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white">
              <option value="dormant">Dormant (unclaimed - free, 1 category, bottom of search)</option>
              <option value="confirmed">Confirmed (claimed &amp; paying - 3 categories)</option>
              <option value="partner">Partner (deeper relationship - +onboarding/NDA)</option>
              <option value="gold">Gold Partner (Partner + independently assured)</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Supplier type</label>
            <select value={L.supplier_type || 'software'} onChange={(e) => patch({ supplier_type: e.target.value })} data-testid="edit-supplier-type" className="w-full mt-1 border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white">
              <option value="software">Software / App</option>
              <option value="hardware">Hardware / Physical</option>
              <option value="managed_service">Managed service on a platform</option>
              <option value="service">Support service</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Company stage</label>
            <select value={L.stage || 'unknown'} onChange={(e) => patch({ stage: e.target.value })} data-testid="edit-stage" className="w-full mt-1 border border-gray-200 rounded-md px-3 py-2.5 text-sm bg-white">
              <option value="unknown">Unknown / not set</option>
              <option value="startup">Startup</option>
              <option value="established">Established</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">Shows as a filter &amp; chip to buyers (paid tiers only).</p>
          </div>
        </div>
      </div>

      {/* Review flags */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mt-5" data-testid="review-flags">
        <h3 className="font-heading font-semibold text-gray-900 mb-1">Assurance <span className="text-xs font-normal text-amber-700">(earned, never for sale)</span></h3>
        <p className="text-sm text-gray-500 mb-3">Flag when our delivery partner (Care City) has independently checked the supplier and/or their product. This is separate from the subscription tier - paying never buys Assurance.</p>
        <div className="flex flex-wrap gap-5">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-testid="edit-supplier-reviewed">
            <input type="checkbox" checked={!!L.supplier_reviewed} onChange={(e) => patch({ supplier_reviewed: e.target.checked })} className="accent-[#00A79D] w-4 h-4" />
            Supplier independently assured
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-testid="edit-product-reviewed">
            <input type="checkbox" checked={!!L.product_reviewed} onChange={(e) => patch({ product_reviewed: e.target.checked })} className="accent-[#00A79D] w-4 h-4" />
            Product independently assured
          </label>
        </div>
      </div>

      {/* Category characteristics tick-list */}
      {((L.categories || []).slice(0, 3)).some((c) => (charCatalogue[c] || []).length) && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 mt-5" data-testid="characteristics-editor">
          <h3 className="font-heading font-semibold text-gray-900 mb-1">Category characteristics</h3>
          <p className="text-sm text-gray-500 mb-4">Tick the essential features this supplier has for each of its Top-3 categories. Shown as green ticks to buyers on paid tiers only.</p>
          {((L.categories || []).slice(0, 3)).filter((c) => (charCatalogue[c] || []).length).map((cat) => {
            const have = (L.characteristics || {})[cat] || [];
            const toggle = (feat) => {
              const set = new Set(have);
              set.has(feat) ? set.delete(feat) : set.add(feat);
              patch({ characteristics: { ...(L.characteristics || {}), [cat]: [...set] } });
            };
            return (
              <div key={cat} className="mb-4">
                <div className="text-sm font-semibold text-gray-800 mb-2">{cat}</div>
                <div className="grid sm:grid-cols-2 gap-1.5">
                  {(charCatalogue[cat] || []).map((feat) => (
                    <label key={feat} className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-testid={`char-${cat}-${feat}`.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}>
                      <input type="checkbox" checked={have.includes(feat)} onChange={() => toggle(feat)} className="accent-[#00A79D] w-4 h-4" />
                      {feat}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Podcast */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mt-5" data-testid="podcast-editor">
        <h3 className="font-heading font-semibold text-gray-900 mb-1">Podcast episode</h3>
        <p className="text-sm text-gray-500 mb-3">Creates a Care Tech Compare podcast post at <code>/podcast/{sel.slug}</code> with Listen on Spotify &amp; Apple Podcasts links. The supplier page links to this post.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <input value={(L.podcast || {}).title || ''} onChange={(e) => patch({ podcast: { ...(L.podcast || {}), title: e.target.value } })} data-testid="edit-podcast-title" placeholder="Episode title" className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm" />
          <input value={(L.podcast || {}).spotify_url || ''} onChange={(e) => patch({ podcast: { ...(L.podcast || {}), spotify_url: e.target.value } })} data-testid="edit-podcast-spotify" placeholder="Spotify episode URL (open.spotify.com/episode/...)" className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm" />
          <input value={(L.podcast || {}).apple_url || ''} onChange={(e) => patch({ podcast: { ...(L.podcast || {}), apple_url: e.target.value } })} data-testid="edit-podcast-apple" placeholder="Apple Podcasts URL (podcasts.apple.com/...)" className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm" />
          <textarea value={(L.podcast || {}).summary || ''} onChange={(e) => patch({ podcast: { ...(L.podcast || {}), summary: e.target.value } })} data-testid="edit-podcast-summary" rows={2} placeholder="Short episode summary" className="w-full sm:col-span-2 border border-gray-200 rounded-md px-3 py-2 text-sm" />
        </div>
      </div>

      {/* Supplier intelligence (internal) */}
      <div className="bg-white border border-amber-200 bg-amber-50/40 rounded-xl p-5 mt-5" data-testid="intel-editor">
        <h3 className="font-heading font-semibold text-gray-900 mb-1">Supplier intelligence <span className="text-xs font-normal text-amber-700">(internal - not shown publicly)</span></h3>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          {[
            ['companies_house', 'Companies House details'],
            ['ctc_status', 'CTC status'],
            ['product_access', 'Product access (enabled/disabled)'],
            ['contract_start', 'Contract start'],
            ['contract_end', 'Contract end'],
            ['contacts', 'Contacts'],
            ['ctg_history', 'CTG history'],
            ['dsr_accreditations', 'DSR / accreditations'],
          ].map(([key, label]) => (
            <div key={key} className={key === 'contacts' || key === 'ctg_history' ? 'sm:col-span-2' : ''}>
              <label className="text-sm font-medium text-gray-700">{label}</label>
              {key === 'contacts' || key === 'ctg_history' ? (
                <textarea value={(L.intel || {})[key] || ''} onChange={(e) => patch({ intel: { ...(L.intel || {}), [key]: e.target.value } })} data-testid={`edit-intel-${key}`} rows={2} className="w-full mt-1 border border-gray-200 rounded-md px-3 py-2 text-sm" />
              ) : (
                <input value={(L.intel || {})[key] || ''} onChange={(e) => patch({ intel: { ...(L.intel || {}), [key]: e.target.value } })} data-testid={`edit-intel-${key}`} className="w-full mt-1 border border-gray-200 rounded-md px-3 py-2.5 text-sm" />
              )}
            </div>
          ))}
        </div>
      </div>


      {/* products */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mt-5">
        <datalist id="care-setting-sizes">
          {['Care home - up to 200 beds', 'Care home - up to 1,000 beds', 'Care home - 1,000+ beds', 'Homecare - small', 'Homecare - medium', 'Homecare - large', 'Supported living - small', 'Supported living - large'].map((o) => <option key={o} value={o} />)}
        </datalist>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-gray-900">Products ({(L.products || []).length})</h3>
          <button onClick={() => patch({ products: [...(L.products || []), { name: '', description: '', image: '' }] })} data-testid="add-product" className="inline-flex items-center gap-1 text-sm font-semibold text-[#00857D] hover:text-[#00A79D]"><Plus className="w-4 h-4" />Add product</button>
        </div>
        <div className="space-y-3">
          {(L.products || []).map((pr, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-3" data-testid={`edit-product-${i}`}>
              <div className="flex gap-2">
                <input placeholder="Product name" value={pr.name || ''} onChange={(e) => setProduct(i, { name: e.target.value })} className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm" />
                <button onClick={() => patch({ products: L.products.filter((_, x) => x !== i) })} className="text-gray-400 hover:text-red-500 px-2"><Trash2 className="w-4 h-4" /></button>
              </div>
              <textarea rows={2} placeholder="Short description" value={pr.description || ''} onChange={(e) => setProduct(i, { description: e.target.value })} className="w-full mt-2 border border-gray-200 rounded-md px-3 py-2 text-sm" />
              <div className="mt-2">
                <label className="text-xs font-medium text-gray-600">Product URL</label>
                <input type="url" value={pr.url || ''} onChange={(e) => setProduct(i, { url: e.target.value })} data-testid={`edit-product-url-${i}`} placeholder="https://www.example.co.uk/product" className="w-full mt-1 border border-gray-200 rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="mt-2">
                <label className="text-xs font-medium text-gray-600">Product categories <span className="font-normal text-gray-400">- pick up to 3, drag to rank</span></label>
                <RankedPicker testid={`edit-product-categories-${i}`} max={3} options={meta.categories} items={pr.categories || []} onChange={(v) => setProduct(i, { categories: v })} />
              </div>
              <div className="mt-2">
                <label className="text-xs font-medium text-gray-600">Care settings &amp; size (comma-separated)</label>
                <input value={csv(pr.settings)} onChange={(e) => setProduct(i, { settings: toArr(e.target.value) })} data-testid={`edit-product-settings-${i}`} placeholder="Homecare - small, Supported living - large" list="care-setting-sizes" className="w-full mt-1 border border-gray-200 rounded-md px-3 py-2 text-sm" />
                <p className="text-[11px] text-gray-400 mt-1">Size-banded options: Care home - up to 200 / up to 1,000 / 1,000+ beds · Homecare - small/medium/large · Supported living - small/large</p>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 mt-2">
                <div>
                  <label className="text-xs font-medium text-gray-600">Market readiness</label>
                  <select value={pr.availability || 'available'} onChange={(e) => setProduct(i, { availability: e.target.value })} data-testid={`edit-product-availability-${i}`} className="w-full mt-1 border border-gray-200 rounded-md px-3 py-2 text-sm bg-white">
                    <option value="available">Live</option>
                    <option value="pilot">In Pilot</option>
                    <option value="in_development">In Development</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Assurance</label>
                  <select value={pr.assurance || 'self_asserted'} onChange={(e) => setProduct(i, { assurance: e.target.value })} data-testid={`edit-product-assurance-${i}`} className="w-full mt-1 border border-gray-200 rounded-md px-3 py-2 text-sm bg-white">
                    <option value="self_asserted">Self-asserted</option>
                    <option value="independently_assured">Independently Assured (Care City)</option>
                  </select>
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer mt-2" data-testid={`edit-product-editable-${i}`}>
                <input type="checkbox" checked={pr.editable_by_customer !== false} onChange={(e) => setProduct(i, { editable_by_customer: e.target.checked })} className="accent-[#00A79D] w-4 h-4" />
                Customer can edit this product in their portal
              </label>
              <div className="mt-2"><AdminImageUpload companyId={sel.id} value={pr.image} onChange={(url) => setProduct(i, { image: url })} label="screenshot" kind="product" testid={`edit-product-image-${i}`} /></div>
              <ProductRichEditor value={pr.rich_content} onChange={(rc) => setProduct(i, { rich_content: rc })} onDraft={() => adminAiDraft(i)} productName={pr.name} uploadFn={adminUpload} testid={`admin-product-rich-${i}`} />
            </div>
          ))}
          {(L.products || []).length === 0 && <p className="text-sm text-gray-400">No products yet.</p>}
        </div>
      </div>

      {/* listing-level rich content (parity with supplier portal) */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mt-5" data-testid="admin-rich-content">
        <div className="flex items-center gap-2 mb-1"><Package className="w-5 h-5 text-[#00A79D]" /><h3 className="font-heading font-semibold text-gray-900">Rich content</h3></div>
        <p className="text-sm text-gray-500 mb-4">Whole-supplier gallery, videos, FAQs, information accordions and testimonials - shown on the public supplier page. This is the same content a supplier can add from their portal.</p>
        <div className="space-y-6">
          <div>
            <label className="text-sm font-semibold text-gray-800">Image gallery / screenshots</label>
            <p className="text-xs text-gray-400 mb-2">Shown as a click-to-enlarge gallery on the public page.</p>
            <GalleryUpload images={L.screenshots || []} onChange={(screenshots) => patch({ screenshots })} testid="admin-listing-gallery" label="images" uploadFn={adminUpload} />
          </div>
          <RichList label="Videos" testid="admin-videos" rows={L.videos || []} fields={[['title', 'Video title'], ['url', 'YouTube/Vimeo URL']]} onChange={(v) => patch({ videos: v })} />
          <RichList label="Information accordions" testid="admin-accordions" rows={L.accordions || []} fields={[['heading', 'Heading'], ['body', 'Body', true]]} onChange={(v) => patch({ accordions: v })} />
          <RichList label="FAQs" testid="admin-faqs" rows={L.faqs || []} fields={[['question', 'Question'], ['answer', 'Answer', true]]} onChange={(v) => patch({ faqs: v })} />
          <RichList label="Testimonials & quotes" testid="admin-testimonials" rows={L.testimonials || []} fields={[['quote', 'Quote', true], ['author', 'Author'], ['role', 'Role / organisation']]} onChange={(v) => patch({ testimonials: v })} />
        </div>
      </div>

      {/* supplier login */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mt-5">
        <div className="flex items-center gap-2 mb-1"><KeyRound className="w-5 h-5 text-[#00A79D]" /><h3 className="font-heading font-semibold text-gray-900">Supplier login</h3></div>
        <p className="text-sm text-gray-500 mb-3">{sel.login_email ? `Current login: ${sel.login_email}. ` : 'No login yet. '}Create a login so the supplier can edit their own listing. They'll get a link to set a password.</p>
        <div className="flex gap-2">
          <input value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="supplier@company.com" data-testid="login-email" className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm" />
          <button onClick={assignLogin} data-testid="assign-login-btn" className="px-4 py-2 bg-[#00A79D] text-white text-sm font-semibold rounded-md hover:bg-[#00857D]">Create login</button>
        </div>
        {loginUrl && (
          <div className="mt-3 bg-[#00A79D]/5 border border-[#00A79D]/20 rounded-md p-3">
            <p className="text-xs text-gray-600 mb-1">Send this set-password link to the supplier:</p>
            <div className="flex items-center gap-2">
              <input readOnly value={loginUrl} className="flex-1 border border-gray-200 rounded px-2 py-1.5 text-xs bg-white" data-testid="login-url" />
              <button onClick={() => { navigator.clipboard.writeText(loginUrl); toast.success('Link copied'); }} className="p-2 text-[#00857D] hover:bg-[#00A79D]/10 rounded"><Copy className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      {/* subscription & access */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 mt-5" data-testid="supplier-subscription">
        <div className="flex items-center gap-2 mb-1"><KeyRound className="w-5 h-5 text-[#00A79D]" /><h3 className="font-heading font-semibold text-gray-900">Subscription &amp; access</h3></div>
        <p className="text-sm text-gray-500 mb-3">
          {account?.tier_label ? `Tier: ${account.tier_label}. ` : ''}
          Set when this supplier's subscription expires, and add extra logins for their team.
        </p>
        {account?.lapsed && (
          <div className="mb-4 flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="supplier-lapsed-banner">
            <span className="text-sm text-amber-800">
              ⚠️ Subscription lapsed{account.lapsed_at ? ` on ${new Date(account.lapsed_at).toLocaleDateString('en-GB')}` : ''} - downgraded from <strong>{account.previous_tier_label || 'paid'}</strong> to free.
            </span>
            <button onClick={reactivate} data-testid="reactivate-btn" className="ml-auto px-4 py-2 bg-amber-600 text-white text-sm font-semibold rounded-md hover:bg-amber-700">Reactivate (restore {account.previous_tier_label || 'paid'} · +1 year)</button>
          </div>
        )}
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Subscription expires</label>
            <input type="date" value={expiryDraft} onChange={(e) => setExpiryDraft(e.target.value)} data-testid="subscription-expiry-input" className="border border-gray-200 rounded-md px-3 py-2 text-sm" />
          </div>
          <button onClick={saveExpiry} data-testid="subscription-expiry-save" className="px-4 py-2 bg-[#00A79D] text-white text-sm font-semibold rounded-md hover:bg-[#00857D]">Save expiry</button>
          {account?.subscription_expires_at && <span className="text-xs text-gray-500">Currently: {new Date(account.subscription_expires_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
        </div>

        <div className="border-t border-gray-100 pt-4">
          <p className="text-xs font-semibold text-gray-600 mb-2">Team logins</p>
          {account?.users?.length ? (
            <ul className="mb-3 space-y-1" data-testid="supplier-users-list">
              {account.users.map((u) => (
                <li key={u.email} className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="font-medium">{u.email}</span>
                  {u.pending && <span className="text-[11px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">invite pending</span>}
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-gray-400 mb-3">No team logins yet.</p>}
          <div className="flex gap-2">
            <input value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} placeholder="colleague@company.com" data-testid="add-user-email" className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm" />
            <button onClick={addUser} data-testid="add-user-btn" className="px-4 py-2 border border-[#00A79D] text-[#00857D] text-sm font-semibold rounded-md hover:bg-[#00A79D]/5">Add user</button>
          </div>
          {newUserUrl && (
            <div className="mt-3 bg-[#00A79D]/5 border border-[#00A79D]/20 rounded-md p-3">
              <p className="text-xs text-gray-600 mb-1">Send this set-password link to the new user:</p>
              <div className="flex items-center gap-2">
                <input readOnly value={newUserUrl} className="flex-1 border border-gray-200 rounded px-2 py-1.5 text-xs bg-white" data-testid="add-user-url" />
                <button onClick={() => { navigator.clipboard.writeText(newUserUrl); toast.success('Link copied'); }} className="p-2 text-[#00857D] hover:bg-[#00A79D]/10 rounded"><Copy className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* sticky save */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-end z-20">
        <button onClick={save} disabled={saving} data-testid="save-supplier" className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#00A79D] text-white text-sm font-semibold rounded-lg hover:bg-[#00857D] disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save changes
        </button>
      </div>
    </div>
  );
};

export default SupplierEditor;
